/**
 * One seam for every unhandled client failure.
 *
 * Reports go to the backend, which relays them into the same Google Cloud
 * Error Reporting stream as server errors, so a browser crash and an API crash
 * land in one place and group the same way. No third-party agent ships with
 * the bundle.
 */
import { API_BASE_URL } from '../config';

export type ClientErrorSource = 'boundary' | 'window' | 'unhandledrejection';

export interface ClientErrorReport {
  source: ClientErrorSource;
  message: string;
  stack?: string;
  componentStack?: string;
  pagePath: string;
  userAgent: string;
  occurredAt: string;
}

const REPORT_ENDPOINT = `${API_BASE_URL}/api/client-errors`;

/* Kept under the field limits the endpoint's model enforces, so a report is
   never rejected for length. */
const MAX_MESSAGE_LENGTH = 1_000;
const MAX_STACK_LENGTH = 8_000;
const REPORT_INTERVAL_MS = 10_000;

/* A crashing render can throw on every retry. Reporting each one would turn a
   broken page into a request flood, so collapse repeats within a short window. */
const lastReportedAt = new Map<string, number>();

const truncate = (value: string, limit: number): string =>
  value.length > limit ? `${value.slice(0, limit - 1)}…` : value;

export const describeError = (error: unknown): { message: string; stack?: string } => {
  if (error instanceof Error) {
    return {
      message: truncate(error.message || error.name || 'Error', MAX_MESSAGE_LENGTH),
      stack: error.stack ? truncate(error.stack, MAX_STACK_LENGTH) : undefined,
    };
  }
  if (typeof error === 'string') return { message: truncate(error, MAX_MESSAGE_LENGTH) };
  try {
    return { message: truncate(JSON.stringify(error), MAX_MESSAGE_LENGTH) };
  } catch {
    return { message: 'Unserializable error' };
  }
};

const shouldReport = (source: ClientErrorSource, message: string): boolean => {
  const key = `${source}:${message}`;
  const now = Date.now();
  const previous = lastReportedAt.get(key);
  if (previous != null && now - previous < REPORT_INTERVAL_MS) return false;
  lastReportedAt.set(key, now);
  return true;
};

const deliver = (report: ClientErrorReport): void => {
  const body = JSON.stringify(report);

  /* sendBeacon survives the page going away, which is exactly when a crash
     report is most likely to be lost. Fall back to fetch when it is absent or
     refuses the payload. */
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const queued = navigator.sendBeacon(
        REPORT_ENDPOINT,
        new Blob([body], { type: 'application/json' }),
      );
      if (queued) return;
    }
  } catch {
    // Fall through to fetch.
  }

  try {
    void fetch(REPORT_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'include',
    }).catch(() => undefined);
  } catch {
    // Reporting must never throw into the failure it is reporting.
  }
};

export const buildClientErrorReport = (
  source: ClientErrorSource,
  error: unknown,
  componentStack?: string,
): ClientErrorReport => {
  const { message, stack } = describeError(error);
  return {
    source,
    message,
    stack,
    componentStack: componentStack
      ? truncate(componentStack, MAX_STACK_LENGTH)
      : undefined,
    pagePath: typeof window === 'undefined' ? '' : window.location.pathname,
    userAgent: typeof navigator === 'undefined' ? '' : navigator.userAgent,
    occurredAt: new Date().toISOString(),
  };
};

export const reportClientError = (
  source: ClientErrorSource,
  error: unknown,
  componentStack?: string,
): void => {
  const report = buildClientErrorReport(source, error, componentStack);

  // Keep the console record even when delivery is suppressed or fails.
  console.error(`[${source}]`, error);

  if (!shouldReport(source, report.message)) return;
  deliver(report);
};

export const resetClientErrorThrottle = (): void => {
  lastReportedAt.clear();
};
